import isDeepEqual from 'fast-deep-equal/es6';
import { createWithEqualityFn } from 'zustand/traditional';
import type { PostsLoaderTypesUnion } from '../posts/loaders';

const initNodeState = Object.freeze({
  checked: false as boolean,
  node: null as PostsLoaderTypesUnion['paginationNodeType'] | null,
});

type NodeState = typeof initNodeState;

const initMultiViewPickerState = Object.freeze({
  shiftKeyPressed: false as boolean,
  isSelectAll: false as boolean,
  selectHistory: [] as string[],
  nodes: new Map<string, NodeState>(),
  selectedNodesCount: 0 as number,
});

type MultiViewPickerState = typeof initMultiViewPickerState;

type MultiViewPickerActions = {
  updateNode: (id: string, node: NodeState | null) => void;
  setShiftKeyPressed: (shiftKeyPressed: boolean) => void;
  selectAll: () => void;
  deselectAll: () => void;
  getSelectedNodes: () => string[];
  getNode: (id: string) => NodeState | undefined;
  updateNodes: (nodes: Map<string, NodeState>) => void;
  reset: () => void;
};

type MultiViewPickerStore = MultiViewPickerState & MultiViewPickerActions;

export const useMultiViewPickerStore = createWithEqualityFn<MultiViewPickerStore>(
  (set, get) => ({
    ...initMultiViewPickerState,
    updateNode: (id, node) => {
      if (!node) {
        return;
      }
      set((state) => {
        let selectedNodesCount = 0;
        const selectHistory = state.selectHistory;

        if (node.checked === true) {
          // if the shift key is pressed, select in range; only when the current node is checked
          onShiftKeyPressed(state.shiftKeyPressed, state.nodes, selectHistory, id);

          // since the shift is pressed we dont know how many nodes are selected
          selectedNodesCount++; // since current node is selected but only updated later
          for (const [_, sNode] of state.nodes) {
            if (sNode.checked) {
              selectedNodesCount++;
            }
          }
          selectHistory.push(id);
        }

        if (node.checked === false) {
          // remove the node from the select history
          const index = selectHistory.indexOf(id);
          if (index > -1) {
            selectHistory.splice(index, 1);
          }
          selectedNodesCount = state.selectedNodesCount - 1;
        }

        state.nodes.set(id, node);

        return {
          nodes: state.nodes,
          selectHistory,
          selectedNodesCount,
        };
      });
    },
    selectAll: () => {
      set((state) => {
        const nodes = new Map<string, NodeState>();
        for (const [id, node] of state.nodes) {
          nodes.set(id, { ...node, checked: true });
        }
        return {
          nodes,
          isSelectAll: true,
          selectHistory: [],
          selectedNodesCount: nodes.size,
        };
      });
    },
    deselectAll: () => {
      set((state) => {
        const nodes = new Map<string, NodeState>();
        for (const [id, node] of state.nodes) {
          nodes.set(id, { ...node, checked: false });
        }
        return { nodes, isSelectAll: false, selectHistory: [], selectedNodesCount: 0 };
      });
    },
    getNode: (id) => {
      return get().nodes.get(id);
    },
    getSelectedNodes: () => {
      const nodes = get().nodes;
      const selectedNodes: string[] = [];
      for (const [id, node] of nodes) {
        if (node.checked) {
          selectedNodes.push(id);
        }
      }
      return selectedNodes;
    },
    setShiftKeyPressed: (shiftKeyPressed) => {
      set({ shiftKeyPressed });
    },
    // This is used to update the nodes when the data changes
    updateNodes: (nodes) => {
      set((state) => {
        // Create a new Map to store the updated nodes
        const updatedNodes = new Map<string, NodeState>();
        let selectedNodesCount = 0;

        // Iterate over the nodes received as an argument
        nodes.forEach((value, key) => {
          const updatedValue = state.nodes.get(key) ?? value ?? initNodeState;
          if (updatedValue.checked) {
            selectedNodesCount++;
          }
          updatedNodes.set(key, updatedValue);
        });

        // Set the state with the updated nodes
        return { nodes: updatedNodes, selectedNodesCount };
      });
    },

    // reset the store on unmount
    reset: () => set({ ...initMultiViewPickerState }),
  }),
  isDeepEqual
);

function onShiftKeyPressed(
  shiftKeyPressed: boolean,
  nodes: (typeof initMultiViewPickerState)['nodes'],
  selectHistory: (typeof initMultiViewPickerState)['selectHistory'],
  toNode: string
) {
  if (!shiftKeyPressed) {
    return;
  }

  const nodesToCheck = [] as [string, NodeState][];
  const lastCheckedNode = selectHistory?.length > 0 ? selectHistory[selectHistory.length - 1] : null;
  if (lastCheckedNode) {
    let start = false;
    let end = false;
    for (const [id, node] of nodes) {
      if (id === lastCheckedNode || id === toNode) {
        if (start) {
          end = true;
        } else {
          start = true;
        }
      }

      if (start && !end) {
        nodesToCheck.push([id, node]);
      }

      if (end) {
        break;
      }
    }
  } else {
    // no last checked node -> we should go from the first node to the current node
    for (const [id, node] of nodes) {
      if (id === toNode) {
        break;
      }

      nodesToCheck.push([id, node]);
    }
  }

  for (const [id, node] of nodesToCheck) {
    nodes.set(id, { ...node, checked: true });
  }
}
